import { ActionIcon, Box, Card, Flex, Group, Menu, Select, Stack, Tooltip } from '@mantine/core';
import { UseQueryOptions } from '@tanstack/react-query';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { Component, ComponentData } from '../../../api/pages-client/pages-client.type';
import { useCreateTarget, useUpdateTarget } from '../../../api/targets-client/targets-client.hooks';
import type { TargetPayload, TransformedTarget } from '../../../api/targets-client/targets-client.type';
import {
  clearTargetForComponent,
  getComponentMetadata,
  setTargetForComponent,
  updateComponentMetadata,
} from '../../../store/dashboards-store/dashboards-store.actions';
import { TileFontSize } from '../../../store/dashboards-store/dashboards-store.type';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { newCOLORS } from '../../../styles/colors';
import { secondaryBase, skyDark } from '../../../styles/design-tokens';
import { Icon } from '../../../ui-library/icon/icon';
import { Text } from '../../../ui-library/typography/typography';
import { TargetModal } from '../../adherence/targets/target-modal';
import {
  calculateTargetThresholds,
  getTargetComparisonSymbol,
  getTimeAllocationTypeLabel,
} from '../../adherence/targets/targets.helpers';
import { DeleteConfirmationModal } from '../../data-management/components/delete-confirmation-modal';
import { getDateRangeDisplayLabel, getWidgetIcon } from '../dashboards.helpers';
import { ChartType } from '../dashboards.type';
import { useComponentDataAsTarget, useWidgetData, useWidgetTargets } from '../hooks/dashboards.hooks';
import { AddWidgetModal } from './add-widget-modal';
import { ChartWidget } from './chart-widget';
import { TableWidget } from './table-widget';
import { TextWidget } from './text-widget';
import { TileWidget } from './tile-widget';
import { getComponentDetailsLink } from './widget.helpers';

import { useInitiatives } from '../../../api/initiative-client/initiative-client.hooks';
import type { Initiative } from '../../../api/initiative-client/initiative-client.type';
import { Button } from '../../../ui-library/button/button';
import { Checkbox } from '../../../ui-library/checkbox/checkbox';

interface DashboardWidgetProps {
  component: Component;
  onEdit?: (updatedWidget: Component) => void;
  onDelete?: () => void;
  isEditingLayout?: boolean;
  isPreview?: boolean;
  previewMetric?: 'tasks' | 'points';
  onPreviewMetricChange?: (metric: 'tasks' | 'points') => void;
}

const defaultComponentData: ComponentData = {
  id: 'default',
  measure: '',
  range_type: 'last_30_days',
  object_type: 'project',
  object_ids: [],
  color_map: {},
};

export const DashboardWidget: FC<DashboardWidgetProps> = ({
  component,
  onEdit,
  onDelete,
  isEditingLayout = false,
  isPreview = false,
  previewMetric,
  onPreviewMetricChange,
}) => {
  const i18n = useTranslation();
  const t = i18n.t;
  const isTextWidget = component.type === 'text';
  const portfolio = useGlobalStore((state) => state.portfolio);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState<TransformedTarget | null>(null);
  const [isAddTargetModalOpen, setIsAddTargetModalOpen] = useState(false);
  const [isEditTargetModalOpen, setIsEditTargetModalOpen] = useState(false);
  const [selectedSprintId, setSelectedSprintId] = useState<string | null>(null);
  const [selectedMetric, setSelectedMetric] = useState<'tasks' | 'points'>(
    isPreview && previewMetric ? previewMetric : 'tasks',
  );

  // Initialize showTrendLine and tile display options from store
  const initialMetadata =
    component.page && component.id ? getComponentMetadata(component.page, component.id) : undefined;
  const [showTrendLine, setShowTrendLine] = useState(initialMetadata?.showTrendLines ?? false);
  const [tileFontSize, setTileFontSize] = useState<TileFontSize>(initialMetadata?.tileFontSize ?? TileFontSize.SMALL);
  const [showTotal, setShowTotal] = useState(initialMetadata?.showTotal ?? true);
  const {
    chartData,
    tableData,
    tileData,
    sprintData,
    measureUnits,
    isLoading: isWidgetDataLoading,
    error,
  } = useWidgetData(component.component_data?.[0] ?? defaultComponentData, component.page);
  const { data: targets, query: targetsQuery } = useWidgetTargets(
    component.component_data?.[0] ?? defaultComponentData,
  );
  const { createTarget, isCreating } = useCreateTarget();
  const { updateTarget, isUpdating } = useUpdateTarget();
  const targetPayload = useComponentDataAsTarget(component.component_data?.[0] ?? defaultComponentData);
  const navigate = useNavigate();

  const isLoading = isWidgetDataLoading || targetsQuery.isLoading;

  const dateRangeLabel = useMemo(
    () => getDateRangeDisplayLabel(component.component_data?.[0]?.range_type, chartData, t),
    [component.component_data, chartData, t],
  );

  const transformedTargets = useMemo(() => {
    if (!targets) return [];

    const flattenedTargets = Object.values(targets).flat();
    const uniqueTargets = flattenedTargets.filter(
      (target, index, self) => index === self.findIndex((t) => t.id === target.id),
    );
    return uniqueTargets;
  }, [targets]);

  // Hydrate selected target and display options from store on mount and when transformedTargets change
  useEffect(() => {
    if (!component.page || !component.id) return;
    const metadata = getComponentMetadata(component.page, component.id);
    const storedTargetId = metadata?.selectedTargetId;
    const storedShowTrendLines = metadata?.showTrendLines ?? false;
    const storedTileFontSize = metadata?.tileFontSize ?? TileFontSize.SMALL;
    const storedShowTotal = metadata?.showTotal ?? true;

    // Sync display options from store
    if (showTrendLine !== storedShowTrendLines) {
      setShowTrendLine(storedShowTrendLines);
    }
    if (tileFontSize !== storedTileFontSize) {
      setTileFontSize(storedTileFontSize);
    }
    if (showTotal !== storedShowTotal) {
      setShowTotal(storedShowTotal);
    }

    // If there's a stored target, verify it exists in current targets
    if (storedTargetId) {
      const targetExists = transformedTargets.some((t) => t.id === storedTargetId);

      if (targetExists) {
        // Target exists, set it if not already selected
        const targetToSet = transformedTargets.find((t) => t.id === storedTargetId) || null;
        if (targetToSet && selectedTarget?.id !== storedTargetId) {
          setSelectedTarget(targetToSet);
        }
      } else if (!isLoading && targetsQuery.isSuccess) {
        // Target no longer exists, clean up from store
        clearTargetForComponent(component.page, component.id);
        if (selectedTarget?.id === storedTargetId) {
          setSelectedTarget(null);
        }
      }
    } else if (selectedTarget && transformedTargets.length > 0) {
      // If no stored target but user has selected one locally, sync to store
      setTargetForComponent(component.page, component.id, selectedTarget.id);
    }
  }, [
    transformedTargets,
    component.page,
    component.id,
    selectedTarget,
    showTrendLine,
    tileFontSize,
    showTotal,
    isLoading,
    targetsQuery.isSuccess,
  ]);

  const { initiatives = [] } = useInitiatives(portfolio?.id, {
    enabled: !!portfolio?.id,
  } as UseQueryOptions<Initiative[]>);
  const teams = useGlobalStore((state) => state.teams);
  const portfolios = useGlobalStore((state) => state.portfolios);

  const getObjectName = useCallback(
    (objectId: string, objectType: string): string => {
      switch (objectType) {
        case 'portfolio':
          return portfolios.find((p) => p.id === objectId)?.name || objectId;
        case 'project':
          return teams.find((t) => t.id === objectId)?.name || objectId;
        case 'board': {
          const board = teams.flatMap((t) => t.subprojects).find((s) => s.id === objectId);
          return board
            ? `${teams.find((t) => t.subprojects.some((s) => s.id === objectId))?.name} - ${board.name}`
            : objectId;
        }
        case 'initiative':
          return initiatives.find((i) => i.id === objectId)?.name || objectId;
        default:
          return objectId;
      }
    },
    [portfolios, teams, initiatives],
  );

  const enhancedTargetData = useMemo(() => {
    if (!selectedTarget) return undefined;

    const componentData = component.component_data?.[0] ?? defaultComponentData;
    const enhancedTarget = { ...selectedTarget };

    if (componentData.object_type === 'portfolio') {
      enhancedTarget.portfolios = componentData.object_ids.map((id) => ({
        id,
        name: getObjectName(id, 'portfolio'),
      }));
    } else {
      enhancedTarget.portfolios = [];
    }

    if (componentData.object_type === 'project') {
      enhancedTarget.projects = componentData.object_ids.map((id) => ({
        id,
        name: getObjectName(id, 'project'),
      }));
    } else {
      enhancedTarget.projects = [];
    }

    if (componentData.object_type === 'board') {
      enhancedTarget.subprojects = componentData.object_ids.map((id) => ({
        id,
        name: getObjectName(id, 'board'),
      }));
    } else {
      enhancedTarget.subprojects = [];
    }

    if (componentData.object_type === 'initiative') {
      enhancedTarget.initiatives = componentData.object_ids.map((id) => {
        const initiative = initiatives.find((i) => i.id === id);
        return {
          id,
          name: getObjectName(id, 'initiative'),
          start_date: initiative?.start_date,
          end_date: initiative?.end_date,
        };
      });
    } else {
      enhancedTarget.initiatives = [];
    }

    return enhancedTarget;
  }, [selectedTarget, component.component_data, getObjectName, initiatives]);

  useEffect(() => {
    if (!selectedTarget) return;
    const updated = transformedTargets.find((t) => t.id === selectedTarget.id) || null;
    if (updated !== selectedTarget) {
      setSelectedTarget(updated);
    }
  }, [transformedTargets, selectedTarget]);

  const isSingleValueWidget = component.type === 'tile' || component.type === 'metric';
  const isWorkPeriodWidget = component.component_data?.[0]?.object_type === 'sprint';
  const isSpecialSprintChart =
    isWorkPeriodWidget &&
    (component.sub_type === 'burn_up' ||
      component.sub_type === 'burn_down' ||
      component.sub_type === 'cumulative_flow');

  useEffect(() => {
    if (!sprintData || sprintData.length === 0) {
      return;
    }
    // Default to the first sprint (the active sprint when it's included, else the
    // most recent completed one), and re-sync if the current selection is no
    // longer in the list — e.g. after editing the widget to toggle the active
    // sprint off, which would otherwise strand selectedSprintId and blank the chart.
    const selectionStillValid = sprintData.some((sprint) => sprint.sprintId === selectedSprintId);
    if (!selectionStillValid) {
      setSelectedSprintId(sprintData[0].sprintId);
    }
  }, [sprintData, selectedSprintId]);

  if (isLoading) {
    return (
      <Card shadow="sm" radius="md" withBorder style={{ height: '100%', maxWidth: '98%' }}>
        <Flex justify="center" align="center" style={{ height: '100%' }}>
          <Text textKey="common.dashboard.loading">Loading...</Text>
        </Flex>
      </Card>
    );
  }

  // Get the selected sprint data
  const selectedSprint = sprintData?.find((sprint) => sprint.sprintId === selectedSprintId);

  const renderWidgetContent = () => {
    switch (component.type) {
      case 'chart':
        return (
          <ChartWidget
            type={(component.sub_type as ChartType) || 'line'}
            data={chartData}
            selectedTarget={selectedTarget}
            componentData={component.component_data?.[0] ?? defaultComponentData}
            targetThresholds={
              selectedTarget
                ? calculateTargetThresholds(chartData, selectedTarget.thresholds, selectedTarget, isWorkPeriodWidget)
                : {}
            }
            selectedSprint={selectedSprint}
            selectedMetric={selectedMetric}
            showTrendLine={showTrendLine}
            measureUnits={measureUnits}
          />
        );
      case 'table':
        return (
          <TableWidget
            data={tableData}
            selectedTarget={selectedTarget}
            targetThresholds={
              selectedTarget
                ? calculateTargetThresholds(tableData, selectedTarget.thresholds, selectedTarget, isWorkPeriodWidget)
                : {}
            }
          />
        );
      case 'tile':
        return (
          <TileWidget data={tileData} selectedTarget={selectedTarget} fontSize={tileFontSize} showTotal={showTotal} />
        );
      case 'metric':
        return <TileWidget data={tileData} selectedTarget={null} fontSize={tileFontSize} showTotal={showTotal} />;
      case 'text':
        return <TextWidget chartData={chartData} componentData={component.component_data?.[0]} />;
      default:
        return null;
    }
  };

  const handleSaveEdit = (updatedWidget: Component) => {
    onEdit?.(updatedWidget);
    setIsEditModalOpen(false);
  };

  const handleDelete = () => {
    onDelete?.();
    setIsDeleteModalOpen(false);
  };

  const handleCreateTarget = async (data: Partial<TargetPayload>) => {
    try {
      await createTarget(data);
      // Refetch targets to update the UI with the newly created target
      await targetsQuery.refetch();
      setIsAddTargetModalOpen(false);
    } catch (error) {
      console.error('Error creating target', error);
    }
  };

  const handleUpdateTarget = async (data: Partial<TargetPayload>) => {
    try {
      await updateTarget(data);
      // Refetch targets to update the UI with the updated target
      await targetsQuery.refetch();
      setIsEditTargetModalOpen(false);
    } catch (error) {
      console.error('Error updating target', error);
    }
  };

  const handleNavigateToDetails = () => {
    const selectedWorkPeriodIdForNav = isWorkPeriodWidget
      ? (selectedSprintId ?? sprintData?.[sprintData.length - 1]?.sprintId ?? null)
      : null;
    const link = getComponentDetailsLink(component, portfolio?.id, selectedWorkPeriodIdForNav);
    if (link) {
      navigate(link);
    }
  };

  const editAndDeleteActions = (
    <Group gap={4}>
      <Tooltip label="Navigate to Details">
        <ActionIcon variant="subtle" size="sm" radius="xl" onClick={() => handleNavigateToDetails()}>
          <Icon name="open_in_new" size={16} style={{ cursor: 'pointer' }} />
        </ActionIcon>
      </Tooltip>
      {!isPreview && (
        <>
          <Tooltip label="Edit Widget">
            <ActionIcon variant="subtle" onClick={() => setIsEditModalOpen(true)} size="sm" radius="xl" color={skyDark}>
              <Icon name="edit" size={16} style={{ cursor: 'pointer' }} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Delete Widget">
            <ActionIcon variant="subtle" onClick={() => setIsDeleteModalOpen(true)} size="sm" radius="xl" color="red">
              <Icon name="delete" size={16} style={{ cursor: 'pointer' }} />
            </ActionIcon>
          </Tooltip>
        </>
      )}
    </Group>
  );

  if (error) {
    return (
      <Card shadow="sm" radius="md" withBorder style={{ height: '95%', maxWidth: '98%' }}>
        <Stack>
          <Flex justify="space-between" align="center">
            <Flex align="center" gap={6}>
              <Icon name={getWidgetIcon(component.type, component.sub_type)} size={18} color={skyDark} />
              <Text>{component.name}</Text>
              <Tooltip
                label={component.description || 'No Description Provided'}
                position="right"
                style={{ maxWidth: 350 }}
                multiline
              >
                <ActionIcon variant="subtle" size="sm" radius="xl">
                  <Icon name="help_outline" size={16} />
                </ActionIcon>
              </Tooltip>
            </Flex>
            {editAndDeleteActions}
          </Flex>
          <Flex justify="center" align="center" style={{ height: '100%', textAlign: 'center' }}>
            <Text color={newCOLORS.red} textKey="common.dashboard.error_loading">
              Error loading data
            </Text>
          </Flex>
        </Stack>
      </Card>
    );
  }

  return (
    <>
      <Card
        key={`widget-${component.id}-${isEditingLayout ? 'editing' : 'normal'}`}
        shadow="md"
        radius="md"
        withBorder
        style={{
          height: '100%',
          maxWidth: '99%',
          ...(isEditingLayout
            ? {
                border: '2px dashed #1d75de',
                backgroundColor: 'rgba(29, 117, 222, 0.05)',
              }
            : {}),
        }}
      >
        {isEditingLayout && (
          <div
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                // react-grid-layout already treats focus + mousedown;
                // trigger a synthetic mouseDown to start the drag
                (e.currentTarget as HTMLElement).dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
              }
            }}
            className="draggable-handle"
            style={{
              height: '24px',
              backgroundColor: 'rgba(29, 117, 222, 0.2)',
              borderTopLeftRadius: '8px',
              borderTopRightRadius: '8px',
              textAlign: 'center',
              cursor: 'move',
            }}
          >
            <Icon name="drag_indicator" size={20} color="#1d75de" />
          </div>
        )}
        <Card.Section inheritPadding py="sm">
          {component.show_title ? (
            <Stack gap="xs">
              <Flex justify="space-between" align="center">
                <Flex align="center" gap={6}>
                  <Icon name={getWidgetIcon(component.type, component.sub_type)} size={18} color={skyDark} />
                  <Text>{component.name}</Text>
                  <Tooltip
                    label={component.description || 'No Description Provided'}
                    position="right"
                    style={{ maxWidth: 350 }}
                    multiline
                  >
                    <ActionIcon variant="subtle" size="sm" radius="xl">
                      <Icon name="help_outline" size={16} />
                    </ActionIcon>
                  </Tooltip>

                  {!isTextWidget && !isSpecialSprintChart && (
                    <>
                      <Tooltip label="Total of all values">
                        <Flex align="center" gap={4}>
                          <Icon name="functions" size={14} />
                          <Text size="tiny" color="dimmed">
                            Total: {tileData.total.toLocaleString()}
                          </Text>
                        </Flex>
                      </Tooltip>
                      <Tooltip label="Average value">
                        <Flex align="center" gap={4}>
                          <Icon name="timeline" size={14} />
                          <Text size="tiny" color="dimmed">
                            Avg: {tileData.average.toLocaleString()}
                          </Text>
                        </Flex>
                      </Tooltip>
                    </>
                  )}
                </Flex>
                {!isEditingLayout && editAndDeleteActions}
              </Flex>

              <Flex justify="space-between" align="center">
                <Group gap={5}>
                  {isSpecialSprintChart && sprintData && sprintData.length > 1 && (
                    <Menu shadow="md" width={200} zIndex={1000}>
                      <Menu.Target>
                        <ActionIcon variant="subtle" size="sm" radius="xl">
                          <Icon name="menu" size={16} style={{ cursor: 'pointer' }} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        {sprintData.map((sprint) => (
                          <Menu.Item
                            key={sprint.sprintId}
                            onClick={() => setSelectedSprintId(sprint.sprintId)}
                            style={{
                              backgroundColor:
                                selectedSprintId === sprint.sprintId ? 'rgba(29, 117, 222, 0.1)' : 'transparent',
                            }}
                          >
                            {sprint.sprintName}
                          </Menu.Item>
                        ))}
                      </Menu.Dropdown>
                    </Menu>
                  )}
                  <Text size="small" color="dimmed">
                    {isSpecialSprintChart && selectedSprint ? selectedSprint.sprintName : dateRangeLabel}
                  </Text>
                </Group>
                {isSpecialSprintChart && (
                  <Select
                    value={selectedMetric}
                    onChange={(value) => {
                      const newMetric = value as 'tasks' | 'points';
                      setSelectedMetric(newMetric);
                      if (isPreview && onPreviewMetricChange) {
                        onPreviewMetricChange(newMetric);
                      }
                    }}
                    data={[
                      { value: 'points', label: `${i18n.t('common.estimation')}` },
                      { value: 'tasks', label: `${i18n.t('common.work_units')}` },
                    ]}
                    size="xs"
                    radius="xl"
                    style={{ width: 100 }}
                    styles={{
                      input: { fontSize: '12px', padding: '4px 8px' },
                      dropdown: { zIndex: 1000 },
                    }}
                    allowDeselect={false}
                  />
                )}
                {!isEditingLayout && !isTextWidget && !isSpecialSprintChart && (
                  <Group gap="sm">
                    {isSingleValueWidget && (
                      <>
                        <Tooltip label="Adjust value font size">
                          <Select
                            value={tileFontSize}
                            onChange={(value) => {
                              if (!value) return;
                              const newSize = value as TileFontSize;
                              setTileFontSize(newSize);
                              if (component.page && component.id) {
                                updateComponentMetadata(component.page, component.id, { tileFontSize: newSize });
                              }
                            }}
                            data={[
                              { value: TileFontSize.SMALL, label: 'Small' },
                              { value: TileFontSize.MEDIUM, label: 'Medium' },
                              { value: TileFontSize.LARGE, label: 'Large' },
                            ]}
                            size="xs"
                            radius="xl"
                            style={{ width: 100 }}
                            styles={{
                              input: { fontSize: '12px', padding: '4px 8px' },
                              dropdown: { zIndex: 1000 },
                            }}
                            allowDeselect={false}
                          />
                        </Tooltip>
                        <Tooltip label="Show the total beneath the value">
                          <Group gap="xs" align="center">
                            <Text size="small">Total</Text>
                            <Checkbox
                              aria-label="Show total"
                              checked={showTotal}
                              onChange={(event) => {
                                const newValue = event.currentTarget.checked;
                                setShowTotal(newValue);
                                if (component.page && component.id) {
                                  updateComponentMetadata(component.page, component.id, { showTotal: newValue });
                                }
                              }}
                            />
                          </Group>
                        </Tooltip>
                      </>
                    )}
                    <Tooltip label="Show trend line(s)">
                      <Group gap="xs" align="center">
                        <Text size="small">Trend</Text>
                        <Checkbox
                          label={undefined}
                          checked={showTrendLine}
                          onChange={(event) => {
                            const newValue = event.currentTarget.checked;
                            setShowTrendLine(newValue);
                            if (component.page && component.id) {
                              updateComponentMetadata(component.page, component.id, { showTrendLines: newValue });
                            }
                          }}
                        />
                      </Group>
                    </Tooltip>
                    {transformedTargets.length > 0 && (
                      <Select
                        data={transformedTargets.map((target) => ({
                          value: target.id,
                          label: target.name || `Target for ${target.measure}`,
                        }))}
                        value={selectedTarget?.id || null}
                        placeholder="Select Target"
                        allowDeselect
                        clearable
                        onChange={(value) => {
                          if (value) {
                            const target = transformedTargets.find((t) => t.id === value) || null;
                            setSelectedTarget(target);
                            if (component.page && component.id) {
                              setTargetForComponent(component.page, component.id, value);
                            }
                          } else {
                            setSelectedTarget(null);
                            if (component.page && component.id) {
                              clearTargetForComponent(component.page, component.id);
                            }
                          }
                        }}
                        radius="lg"
                        w="175px"
                        size="xs"
                        leftSection={
                          selectedTarget ? (
                            <Tooltip
                              multiline
                              label={
                                <Stack gap={0}>
                                  <Text size="small" weight="bold">
                                    {selectedTarget.name}
                                  </Text>
                                  <Text size="tiny">
                                    {`${selectedTarget.measure}: 
                      ${getTargetComparisonSymbol(
                        selectedTarget.target_comparison,
                        selectedTarget.target_value,
                        selectedTarget.target_value_upper,
                      )} 
                    ${getTimeAllocationTypeLabel(selectedTarget.time_allocation_type) ?? ''}`}
                                  </Text>
                                  <Text size="tiny">
                                    {`${new Date(selectedTarget.start_date).toLocaleDateString()} - ${
                                      selectedTarget.end_date
                                        ? new Date(selectedTarget.end_date).toLocaleDateString()
                                        : 'Present'
                                    }`}
                                  </Text>
                                </Stack>
                              }
                            >
                              <Icon name="info" size={14} />
                            </Tooltip>
                          ) : null
                        }
                      />
                    )}

                    {selectedTarget?.id && (
                      <Tooltip label="Edit Target">
                        <Icon
                          name="edit"
                          size={20}
                          color={secondaryBase}
                          style={{ cursor: 'pointer' }}
                          onClick={() => setIsEditTargetModalOpen(true)}
                        />
                      </Tooltip>
                    )}

                    {transformedTargets.length > 0 && (
                      <Tooltip label="Add Target">
                        <Icon
                          name="add_circle_outline"
                          size={20}
                          color={secondaryBase}
                          style={{ cursor: 'pointer' }}
                          onClick={() => setIsAddTargetModalOpen(true)}
                        />
                      </Tooltip>
                    )}

                    {transformedTargets.length === 0 && (
                      // shows only a large Create Target button when no targets have been created yet
                      <Tooltip label="Create Target">
                        <Button
                          variant="secondary"
                          size="xs"
                          radius="xl"
                          rightSection={<Icon name="add_circle_outline" size={16} color="white" />}
                          onClick={() => setIsAddTargetModalOpen(true)}
                        >
                          Create Target
                        </Button>
                      </Tooltip>
                    )}
                  </Group>
                )}
              </Flex>
            </Stack>
          ) : (
            <Flex justify="flex-end" align="center">
              {!isEditingLayout && editAndDeleteActions}
            </Flex>
          )}
        </Card.Section>

        <Box
          style={{
            height: component.type === 'chart' ? 'calc(100% - 80px)' : 'auto',
            // Card is a flex column, so single-value widgets stretch to the
            // remaining height and center their content within it
            ...(isSingleValueWidget ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : {}),
          }}
        >
          {renderWidgetContent()}
        </Box>
      </Card>

      <AddWidgetModal
        opened={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleSaveEdit}
        initialData={component}
        isEditing
      />

      <DeleteConfirmationModal
        itemName={component.name}
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onDelete={handleDelete}
        title="Delete Widget"
        isDeleting={false}
      />

      <TargetModal
        opened={isAddTargetModalOpen}
        onClose={() => setIsAddTargetModalOpen(false)}
        onSave={handleCreateTarget}
        initialData={targetPayload}
        isCreating={true}
        isLoading={isCreating}
        includeDashboardMeasures={true}
      />

      <TargetModal
        opened={isEditTargetModalOpen}
        onClose={() => setIsEditTargetModalOpen(false)}
        onSave={handleUpdateTarget}
        initialData={enhancedTargetData}
        isCreating={false}
        isLoading={isUpdating}
        includeDashboardMeasures={true}
      />
    </>
  );
};
